<template>
  <div class="canvas-controls">
    <template v-if="showTextControls">
      <text-control-button text="Delete" @click="onDeleteText">
        <delete-icon class="canvas-controls__button-icon" />
      </text-control-button>
      <text-control-button text="Edit" @click="editText">
        <edit-icon class="canvas-controls__button-icon" />
      </text-control-button>
    </template>
    <template v-else-if="showStickerControls">
      <text-control-button text="Delete" @click="onDeleteSticker">
        <delete-icon class="canvas-controls__button-icon" />
      </text-control-button>
      <text-control-button text="Flip" @click="flipSticker">
        <flip-icon class="canvas-controls__button-icon"/>
      </text-control-button>
    </template>
    <template v-else-if="showBackgroundControls">
      <text-control-button text="Flip" @click="flipBackground">
        <flip-icon class="canvas-controls__button-icon"/>
      </text-control-button>
      <text-control-button text="Fill" @click="changeToggledState" v-if="projectStore.background.backgroundScale === 'fit'">
        <fill-toggle-icon class="canvas-controls__button-icon"/>
      </text-control-button>
      <text-control-button text="Fit" @click="changeToggledState" v-else>
        <fit-toggle-icon class="canvas-controls__button-icon"/>
      </text-control-button>
    </template>
  </div>
</template>
<script setup lang="ts">
import TextControlButton from '@/components/preview/TextControlButton.vue'
import DeleteIcon from '@/assets/DeleteIcon.svg'
import EditIcon from '@/assets/EditIcon.svg'
import FillToggleIcon from '@/assets/FillToggleIcon.svg'
import FitToggleIcon from '@/assets/FitToggleIcon.svg'
import FlipIcon from '@/assets/FlipIcon.svg'
import { useProjectStore } from '@/store/project-store'
import { useApplicationStore } from '@/store/application-store'
import useTextStates from '@/composables/UseTextStates'
import { computed } from 'vue'
import { TextImplementation } from '@/composables/UseTextCanvas'
import * as PIXI from 'pixi.js'
import { DisplayObjectPair } from '@/pixi/types'
import useDisplayObject from '@/composables/UseDisplayObject'
import useCanvasEventHandler from '@/composables/UseCanvasEventHandler'
import { BackgroundCanvasState } from '@/composables/UseBackgroundCanvas'

const projectStore = useProjectStore()
const applicationStore = useApplicationStore()

const { editText } = useTextStates()
const { getDisplayObjectSprite, updateDisplayObjState } = useDisplayObject()
const { onDeleteSticker, onDeleteText } = useCanvasEventHandler()

const props = defineProps<{
  backgroundState: BackgroundCanvasState,
  textDisplayObjectsMap: Map<number, DisplayObjectPair>,
  stickerDisplayObjectsMap: Map<number, DisplayObjectPair>,
  selectedTextImplementation?: TextImplementation
}>()

const showBackgroundControls = computed(() => !applicationStore.selectedSticker && !applicationStore.selectedText)
const showStickerControls = computed(() => applicationStore.selectedSticker)
const showTextControls = computed(() => applicationStore.selectedText && props.selectedTextImplementation?.isTextRasterised && (applicationStore.editTextState === 'none'))

const changeToggledState = (): void => {
  projectStore.toggleBackgroundScale()
}

const flipSticker = (): void => {
  if (applicationStore.selectedSticker) {
    const stickerSprite = getDisplayObjectSprite(applicationStore.selectedSticker, props.textDisplayObjectsMap, props.stickerDisplayObjectsMap)
    if (stickerSprite) {
      flipSprite(stickerSprite)
      updateDisplayObjState(applicationStore.selectedSticker, props.textDisplayObjectsMap, props.stickerDisplayObjectsMap)
    }
  }
}

const flipBackground = (): void => {
  if (props.backgroundState.sprite) {
    flipSprite(props.backgroundState.sprite)
  }
}

const flipSprite = (sprite: PIXI.Sprite): void => {
  // 12 is to Mirror Horizontally, 0 is no rotation
  sprite.texture.rotate = sprite.texture.rotate === 12 ? 0 : 12
}

</script>

<style lang="scss">
.canvas-controls {
  &__button-icon {
    height: 36px;
    width: 36px;
  }
}
</style>